import React, {useState, useEffect} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { friendlyAuthError } from "../utils/authErrors";
import sigmaLogo from "../Images/sigma-bank-logo.png";

function SignIn() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    
    const location = useLocation();
    const navigate = useNavigate();
    const { signIn, user } = useAuth();

    // Handle role from location state
    useEffect(() => {
        if (location.state?.role) {
            localStorage.setItem("role", location.state.role);
        }
    }, [location.state]);

    // Redirect if already logged in
    useEffect(() => {
        if (user) {
            navigate('/welcome');
        }
    }, [user, navigate]);

    const handleSignIn = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        if (!email || !password) {
            setError('Please enter both email and password to continue.');
            setLoading(false);
            return;
        }

        const { user: signedInUser, error: signInError } = await signIn(email, password);

        if (signInError) {
            setError(friendlyAuthError(signInError));
            setLoading(false);
        } else if (signedInUser) {
            // Defensive only — Supabase returns an error rather than a user for
            // an unconfirmed account, so this branch is not the path an
            // unverified sign-in actually takes. friendlyAuthError handles that.
            if (!signedInUser.email_confirmed_at) {
                setError('Please verify your email address before signing in. Check your inbox for the verification link.');
                setLoading(false);
            } else {
                // Success - navigate to welcome page
                navigate('/welcome');
            }
        }
    };

    return (
        <main className="main-sectionWrapper">
            <div id="main-section" style={styles.mainSection}>
                {/* Background Gradient */}
                <div style={styles.background}></div>

                {/* Desktop Notice - Only visible on desktop */}
                <div style={styles.desktopNotice} className="desktop-notice-signin">
                    <div style={styles.noticeCard}>
                        <div style={styles.noticeLogoCircle}>
                            <img src={sigmaLogo} alt="Sigma Bank" style={styles.noticeLogoImg} />
                        </div>
                        <h2 style={styles.noticeTitle}>Mobile Access Required</h2>
                        <p style={styles.noticeText}>
                            User accounts can only be accessed through mobile devices.
                            Please use your smartphone or tablet to sign in.
                        </p>
                        <div style={styles.noticeDivider}></div>
                        <p style={styles.noticeSubtext}>
                            <i className="bx bx-info-circle" style={styles.noticeInfoIcon}></i>
                            Are you an administrator? 
                        </p>
                        <Link to="/admin/login" style={styles.adminLinkButton}>
                            <i className="bx bx-shield-alt-2"></i>
                            <span>Go to Admin Login</span>
                            <i className="bx bx-right-arrow-alt"></i>
                        </Link>
                        <Link to="/" style={styles.backHomeLink}>
                            <i className="bx bx-arrow-back"></i>
                            <span>Back to Home</span>
                        </Link>
                    </div>
                </div>

                {/* Content Container - Mobile Only */}
                <div style={styles.container} className="mobile-signin-form">
                    {/* Logo/Brand */}
                    <div style={styles.brandContainer}>
                        <div style={styles.logoCircle}>
                            <img src={sigmaLogo} alt="Sigma Bank" style={styles.logoImage} />
                        </div>
                        <p style={styles.brandTagline}>Secure Digital Banking</p>
                    </div>

                    {/* Auth Card */}
                    <div style={styles.authCard}>
                        {/* Welcome Text */}
                        <div style={styles.welcomeSection}>
                            <h2 style={styles.welcomeTitle}>Welcome Back!</h2>
                            <p style={styles.welcomeSubtitle}>Sign in to continue to your dashboard</p>
                        </div>

                        {/* Form */}
                        <form onSubmit={handleSignIn} style={styles.form}>
                            {/* Email Field */}
                            <div style={styles.formGroup}>
                                <label style={styles.label}>
                                    <i className="bx bx-envelope" style={styles.labelIcon}></i>
                                    Email Address
                                </label>
                                <input 
                                    type="email" 
                                    name="email" 
                                    placeholder="Enter your email" 
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)} 
                                    disabled={loading}
                                    style={styles.input}
                                />
                            </div>

                            {/* Password Field */}
                            <div style={styles.formGroup}>
                                <label style={styles.label}>
                                    <i className="bx bx-lock-alt" style={styles.labelIcon}></i>
                                    Password
                                </label>
                                <div style={styles.passwordWrapper}>
                                    <input 
                                        type={showPassword ? 'text' : 'password'} 
                                        name="password" 
                                        placeholder="Enter your password" 
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)} 
                                        disabled={loading}
                                        style={styles.input}
                                    />
                                    <button 
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        style={styles.eyeButton}
                                    >
                                        <i className={`bx ${showPassword ? 'bx-hide' : 'bx-show'}`}></i>
                                    </button>
                                </div>
                                <Link to="/forgot-password" style={styles.forgotLink}>
                                    Forgot password?
                                </Link>
                            </div>

                            {/* Error Message */}
                            {error && (
                                <div style={styles.errorBox}>
                                    <i className="bx bx-error-circle" style={styles.errorIcon}></i>
                                    <span style={styles.errorText}>{error}</span>
                                </div>
                            )}

                            {/* Submit Button */}
                            <button 
                                type="submit" 
                                style={{
                                    ...styles.submitButton,
                                    opacity: loading ? 0.7 : 1,
                                    cursor: loading ? 'not-allowed' : 'pointer'
                                }}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <div style={styles.spinner}></div>
                                        <span>Signing In...</span>
                                    </>
                                ) : (
                                    <>
                                        <span>Sign In</span>
                                        <i className="bx bx-right-arrow-alt" style={styles.buttonIcon}></i>
                                    </>
                                )}
                            </button>
                        </form>

                        {/* Footer */}
                        <div style={styles.footer}>
                            <p style={styles.footerText}>
                                Don't have an account?{' '}
                                <Link 
                                    to="/sign-up" 
                                    state={{ role: localStorage.getItem("role") }}
                                    style={styles.footerLink}
                                >
                                    Sign Up Now
                                </Link>
                            </p>
                        </div>
                    </div>

                    {/* Bottom Decoration */}
                    <div style={styles.bottomDecor}>
                        <div style={styles.decorCircle1}></div>
                        <div style={styles.decorCircle2}></div>
                    </div>
                </div>
            </div>

            {/* Responsive CSS for mobile-only access */}
            <style>{`
                /* Hide mobile form on desktop, show notice */
                @media (min-width: 769px) {
                    .mobile-signin-form {
                        display: none !important;
                    }
                    .desktop-notice-signin {
                        display: flex !important;
                    }
                }

                /* Hide desktop notice on mobile, show form */
                @media (max-width: 768px) {
                    .desktop-notice-signin {
                        display: none !important;
                    }
                    .mobile-signin-form {
                        display: block !important;
                    }
                }
            `}</style>
        </main>
    );
}

const styles = {
    mainSection: {
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        position: 'relative',
        overflow: 'hidden',
    },
    background: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(135deg, #0e0758 0%, #1a0d4d 50%, #0e0758 100%)',
        zIndex: -1,
    },
    container: {
        width: '100%',
        maxWidth: '440px',
        margin: '0 auto',
        position: 'relative',
        zIndex: 1,
    },
    brandContainer: {
        textAlign: 'center',
        marginBottom: '30px',
    },
    logoCircle: {
        width: '90px',
        height: '90px',
        borderRadius: '50%',
        background: '#FFFFFF',
        border: '2px solid rgba(25, 191, 177, 0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 15px',
        boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
    },
    logoImage: {
        width: '55px',
        height: 'auto',
    },
    brandTagline: {
        fontSize: '14px',
        color: 'rgba(255, 255, 255, 0.7)',
        margin: 0,
    },
    authCard: {
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(20px)',
        borderRadius: '25px',
        padding: '30px 25px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    },
    tabContainer: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '10px',
        marginBottom: '25px',
        background: 'rgba(255, 255, 255, 0.05)',
        padding: '5px',
        borderRadius: '15px',
    },
    tab: {
        padding: '12px',
        textAlign: 'center',
        fontSize: '14px',
        fontWeight: '600',
        color: 'rgba(255, 255, 255, 0.6)',
        borderRadius: '12px',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
    },
    tabActive: {
        background: 'linear-gradient(135deg, #19bfb1 0%, #0e9688 100%)',
        color: '#fff',
        boxShadow: '0 4px 15px rgba(25, 191, 177, 0.3)',
    },
    welcomeSection: {
        textAlign: 'center',
        marginBottom: '25px',
    },
    welcomeTitle: {
        fontSize: '24px',
        fontWeight: '700',
        color: '#fff',
        margin: '0 0 8px 0',
    },
    welcomeSubtitle: {
        fontSize: '14px',
        color: 'rgba(255, 255, 255, 0.6)',
        margin: 0,
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: '18px',
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    label: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '13px',
        fontWeight: '600',
        color: 'rgba(255, 255, 255, 0.9)',
        marginBottom: '5px',
    },
    labelIcon: {
        fontSize: '16px',
        color: '#19bfb1',
    },
    input: {
        width: '100%',
        padding: '14px 16px',
        fontSize: '16px',
        color: '#fff',
        background: 'rgba(255, 255, 255, 0.08)',
        border: '1px solid rgba(255, 255, 255, 0.15)',
        borderRadius: '12px',
        outline: 'none',
        transition: 'all 0.3s ease',
        boxSizing: 'border-box',
    },
    passwordWrapper: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
    },
    eyeButton: {
        position: 'absolute',
        right: '12px',
        background: 'none',
        border: 'none',
        color: 'rgba(255, 255, 255, 0.5)',
        fontSize: '20px',
        cursor: 'pointer',
        padding: '5px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    forgotLink: {
        alignSelf: 'flex-end',
        fontSize: '13px',
        color: '#19bfb1',
        textDecoration: 'none',
        fontWeight: '500',
        marginTop: '5px',
    },
    errorBox: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        background: 'rgba(220, 53, 69, 0.15)',
        border: '1px solid rgba(220, 53, 69, 0.3)',
        borderRadius: '12px',
        padding: '12px 15px',
    },
    errorIcon: {
        fontSize: '20px',
        color: '#dc3545',
        flexShrink: 0,
    },
    errorText: {
        fontSize: '13px',
        color: '#ff6b7a',
        lineHeight: '1.5',
    },
    submitButton: {
        width: '100%',
        padding: '16px',
        fontSize: '15px',
        fontWeight: '600',
        color: '#fff',
        background: 'linear-gradient(135deg, #19bfb1 0%, #0e9688 100%)',
        border: 'none',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        boxShadow: '0 8px 20px rgba(25, 191, 177, 0.3)',
        transition: 'all 0.3s ease',
        marginTop: '8px',
    },
    buttonIcon: {
        fontSize: '20px',
    },
    spinner: {
        width: '18px',
        height: '18px',
        border: '2px solid rgba(255, 255, 255, 0.3)',
        borderTop: '2px solid #fff',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
    footer: {
        textAlign: 'center',
        marginTop: '25px',
        paddingTop: '20px',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    },
    footerText: {
        fontSize: '14px',
        color: 'rgba(255, 255, 255, 0.7)',
        margin: 0,
    },
    footerLink: {
        color: '#19bfb1',
        fontWeight: '600',
        textDecoration: 'none',
    },
    bottomDecor: {
        position: 'fixed',
        bottom: '-100px',
        left: '-100px',
        right: 0,
        height: '300px',
        zIndex: -1,
        pointerEvents: 'none',
    },
    decorCircle1: {
        position: 'absolute',
        width: '250px',
        height: '250px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(25, 191, 177, 0.15) 0%, transparent 70%)',
        bottom: '0',
        left: '10%',
    },
    decorCircle2: {
        position: 'absolute',
        width: '350px',
        height: '350px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(14, 7, 88, 0.4) 0%, transparent 70%)',
        bottom: '-50px',
        right: '10%',
    },
    // Desktop Notice Styles
    desktopNotice: {
        display: 'none', // Hidden by default, shown on desktop via CSS
        width: '100%',
        maxWidth: '550px',
        margin: '0 auto',
        position: 'relative',
        zIndex: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
    noticeCard: {
        background: 'rgba(255, 255, 255, 0.08)',
        backdropFilter: 'blur(20px)',
        borderRadius: '25px',
        padding: '50px 40px',
        border: '1px solid rgba(255, 255, 255, 0.15)',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        textAlign: 'center',
    },
    noticeIcon: {
        width: '80px',
        height: '80px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #19bfb1 0%, #0e9688 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 25px',
        fontSize: '40px',
        color: '#fff',
        boxShadow: '0 10px 30px rgba(25, 191, 177, 0.4)',
    },
    noticeLogoCircle: {
        width: '100px',
        height: '100px',
        borderRadius: '50%',
        background: '#FFFFFF',
        border: '2px solid rgba(25, 191, 177, 0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 25px',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
    },
    noticeLogoImg: {
        width: '60px',
        height: 'auto',
    },
    noticeTitle: {
        fontSize: '28px',
        fontWeight: '700',
        color: '#fff',
        margin: '0 0 15px 0',
    },
    noticeText: {
        fontSize: '15px',
        color: 'rgba(255, 255, 255, 0.8)',
        lineHeight: '1.7',
        margin: '0 0 30px 0',
    },
    noticeDivider: {
        width: '60px',
        height: '3px',
        background: 'linear-gradient(135deg, #19bfb1 0%, #0e9688 100%)',
        margin: '30px auto',
        borderRadius: '2px',
    },
    noticeSubtext: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        fontSize: '14px',
        color: 'rgba(255, 255, 255, 0.7)',
        margin: '0 0 20px 0',
    },
    noticeInfoIcon: {
        fontSize: '18px',
        color: '#19bfb1',
    },
    adminLinkButton: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '10px',
        padding: '14px 28px',
        background: 'linear-gradient(135deg, #19bfb1 0%, #0e9688 100%)',
        color: '#fff',
        fontSize: '15px',
        fontWeight: '600',
        textDecoration: 'none',
        borderRadius: '12px',
        boxShadow: '0 8px 20px rgba(25, 191, 177, 0.3)',
        transition: 'all 0.3s ease',
        marginBottom: '15px',
    },
    backHomeLink: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 20px',
        color: 'rgba(255, 255, 255, 0.7)',
        fontSize: '14px',
        fontWeight: '500',
        textDecoration: 'none',
        transition: 'color 0.2s ease',
        marginTop: '10px',
    },
};

export default SignIn;
